import { DataType, FormDialogMode } from '@globaleris/ui.library'
import { CardProps } from '@material-ui/core'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumPeriodoLiquidacion } from '../../../api'
import { PriceCoefficientTable } from '../../../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import { EnumRedondeo } from '../../../api/Argentia/enums'
import { enumToSelectOptionArray } from '../../../helpers'
import { gridValuesGenerator } from '../../../helpers/gridValuesGenerator/gridValuesGenerator'
import { FormDialogDefinitionProps, useFormDialog } from '../../../hooks'
import { CardActionProps } from '../cardActions/cardActions'
import { PriceCoefficientsTable } from '../priceCoefficientsTable/priceCoefficientsTable'
import { ButtonOption } from '../splitButton/splitButton'
import { StandardCard } from '../standardCard/standardCard'
import { gridRowToPriceCoefficientList } from './priceCoefficientsCard.mapper'

enum ComponentAddButtonOptions {
  ASSISTED = 1,
  MANUAL
}

const INITIAL_PRICE_COEFFICIENT_TABLE = {
  year: new Date()
}

interface PriceCoefficientsCardProps extends CardProps {
  priceCoefficientsList?: PriceCoefficientTable[] | undefined | null
  rounding: EnumRedondeo
  isLoading?: boolean
  onTableDataChange?: (state: PriceCoefficientTable[]) => void
  emptyButtonCallback?: () => void
  showSaveHandler?: boolean // This optional property is to handle saving callback when save formButton is not enabled. default set to FALSE
  saveButtonDisabled?: boolean
  onSaveHandler?: () => void // This optional property is to handle saving callback when save header Button is not available/possible
}

export const PriceCoefficientsCard: React.FC<PriceCoefficientsCardProps> = ({
  priceCoefficientsList,
  rounding,
  isLoading = false,
  onTableDataChange = () => {},
  emptyButtonCallback = () => {},
  showSaveHandler = false,
  saveButtonDisabled = false,
  onSaveHandler = () => {},
  ...props
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'priceCoefficient', 'priceComponent'])
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const [tableData, setTableData] = React.useState<PriceCoefficientTable[]>(
    priceCoefficientsList ?? []
  )
  const [selectedCoefficient, setSelectedCoefficient] = useState<PriceCoefficientTable>(
    INITIAL_PRICE_COEFFICIENT_TABLE as PriceCoefficientTable
  )

  const noData = tableData === undefined || tableData === null || tableData?.length === 0

  // #region Handlers Starts

  // ------------- Price Coefficients Table internal Handlers ---------------- //

  const handleEditRow = (coefficient: PriceCoefficientTable) => {
    const coefficientItem = tableData?.find(
      c => (c.id && c.id === coefficient.id) || (!c.id && c.tempId === coefficient.tempId)
    )

    const updatedCoefficient = {
      ...coefficientItem,
      ...coefficient,
      tempId: `${coefficientItem?.tempId ?? coefficientItem?.id}`
    } as any

    const newCoefficientList = tableData!.map((coefficient, index) => {
      if (
        (coefficient.id && coefficient.id === updatedCoefficient.id) ||
        (!coefficient.id && coefficient.tempId === updatedCoefficient.tempId)
      ) {
        return updatedCoefficient
      }
      return coefficient
    })

    setTableData(newCoefficientList)
    onTableDataChange(newCoefficientList)
  }

  const handleDeleteRow = (id: number | string) => {
    const newCoefficientList = tableData!.filter(
      coefficient =>
        (coefficient.id && coefficient.id !== id) || (!coefficient.id && coefficient.tempId !== id)
    )
    setTableData(newCoefficientList)
    onTableDataChange(newCoefficientList)
  }

  // ----------------- FooterActions Internal Handlers -------------------- //
  const handleOnAddCoefficients = (options: ButtonOption) => {
    switch (options.id) {
      case ComponentAddButtonOptions.MANUAL:
        onAddManualValuesActionHandler()
        break
      case ComponentAddButtonOptions.ASSISTED:
        openAssistantModal()
        break
    }
  }

  const openAssistantModal = () => {
    openForm({
      definition: coefficientAssistantDefinition,
      title: t('priceCoefficient:forms.assistantForm.title'),
      okBtnLabel: t('priceCoefficient:forms.assistantForm.actions.okButton'),
      cancelBtnLabel: t('priceCoefficient:forms.assistantForm.actions.cancelButton'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: formData => {
        formData ? handleGenerateValues(formData) : closeForm()
      }
    })
  }

  const onAddManualValuesActionHandler = async () => {
    setSelectedCoefficient({
      ...INITIAL_PRICE_COEFFICIENT_TABLE,
      manual: true,
      indefinite: false
    } as PriceCoefficientTable)

    openForm({
      definition: addManualDefinition,
      title: t('priceCoefficient:forms.editValues.title'),
      okBtnLabel: t('priceCoefficient:forms.editValues.actions.okButton'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: formData => {
        formData ? addManualModule(formData) : closeForm()
      }
    })
    return true
  }

  const addManualModule = async (coefficient: PriceCoefficientTable) => {
    const newCoefficientList =
      tableData && tableData.length > 0 ? [...tableData, coefficient] : [coefficient]
    setTableData(newCoefficientList)
    onTableDataChange(newCoefficientList)
    // setShowCoefficients(true)
    closeForm()
  }

  const onSaveHandlerClick = () => {
    onSaveHandler()
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const handleGenerateValues = formData => {
    const startDate = moment(formData.year).startOf('year').toDate()
    const endDate = moment(formData.year).endOf('year').toDate()
    const generatedValues = gridValuesGenerator({
      startDate,
      endDate,
      period: formData.idSettlementPeriod,
      fixedQuantity: null
    })

    if (generatedValues?.value) {
      const newCoefficientList =
        tableData && tableData.length > 0
          ? [
              ...tableData,
              ...gridRowToPriceCoefficientList(generatedValues.value!, formData.idSettlementPeriod)
            ]
          : gridRowToPriceCoefficientList(generatedValues.value!, formData.idSettlementPeriod)

      setTableData(newCoefficientList)
      onTableDataChange(newCoefficientList)
    }
    // setShowCoefficients(true)
    closeForm()
  }
  // #endregion Functions Ends

  // #region Form Definition Starts

  const addManualDefinition: FormDialogDefinitionProps =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_PRICE_VALUES',
        sections: [
          {
            inputs: [
              {
                name: 'periodDescription',
                label: t('priceComponent:forms.editValues.inputs.description'),
                type: DataType.TEXT,
                required: true,
                onChange(value, formState) {
                  setSelectedCoefficient(prevState => ({
                    ...prevState,
                    periodDescription: value.toString()
                  }))
                }
              },
              {
                name: 'idSettlementPeriod',
                label: t('priceComponent:forms.editValues.inputs.settlementPeriod'),
                type: DataType.SELECT,
                required: true,
                selectOptions: enumToSelectOptionArray(
                  EnumPeriodoLiquidacion,
                  'enums:EnumPeriodoLiquidacion'
                ).filter(option => option.value !== EnumPeriodoLiquidacion.Horario),
                onChange(value, formState) {
                  setSelectedCoefficient(prevState => ({
                    ...prevState,
                    idSettlementPeriod: +value
                  }))
                }
              },
              {
                name: 'startDate',
                label: t('priceComponent:forms.editValues.inputs.startDate'),
                type: DataType.DATE,
                required: true,
                readOnly: selectedCoefficient.indefinite,
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const date = moment(value).startOf('month').toDate()
                    setSelectedCoefficient(prevState => ({
                      ...prevState,
                      startDate: date
                    }))
                  }
                }
              },
              {
                name: 'endDate',
                label: t('priceComponent:forms.editValues.inputs.endDate'),
                type: DataType.DATE,
                required: !selectedCoefficient.indefinite,
                readOnly: selectedCoefficient.indefinite,
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const date = moment(value).endOf('month').toDate()
                    setSelectedCoefficient(prevState => ({
                      ...prevState,
                      endDate: date
                    }))
                  }
                }
              },
              {
                name: 'indefinite',
                label: t('priceComponent:forms.editValues.inputs.indefinite'),
                type: DataType.SWITCH,
                clearable: true,
                display: selectedCoefficient.manual,
                onChange(value, formState) {
                  setSelectedCoefficient(prevState => ({
                    ...prevState,
                    indefinite: Boolean(value),
                    startDate: Boolean(value)
                      ? moment(new Date()).startOf('year').toDate()
                      : prevState.startDate,
                    endDate: undefined
                  }))
                }
              },
              {
                name: 'p1',
                label: t('priceComponent:forms.editValues.inputs.p1'),
                customDecimalScale: rounding ?? 6,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p2',
                label: t('priceComponent:forms.editValues.inputs.p2'),
                customDecimalScale: rounding ?? 6,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p3',
                label: t('priceComponent:forms.editValues.inputs.p3'),
                customDecimalScale: rounding ?? 6,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p4',
                label: t('priceComponent:forms.editValues.inputs.p4'),
                customDecimalScale: rounding ?? 6,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p5',
                label: t('priceComponent:forms.editValues.inputs.p5'),
                customDecimalScale: rounding ?? 6,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p6',
                label: t('priceComponent:forms.editValues.inputs.p6'),
                customDecimalScale: rounding ?? 6,
                type: DataType.NUMBER,
                clearable: true
              }
            ]
          }
        ]
      }
    }, [selectedCoefficient])

  const coefficientAssistantDefinition: FormDialogDefinitionProps =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'COEFFICIENT_ASSISTANT',
        sections: [
          {
            inputs: [
              {
                name: 'subtitleHelpText',
                labelTitle: t('priceComponent:forms.coefficientAssistant.inputs.subtitleHelpText'),
                type: DataType.LABEL
              },
              {
                name: 'idSettlementPeriod',
                label: t('priceComponent:forms.coefficientAssistant.inputs.settlementPeriod'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumPeriodoLiquidacion,
                  'enums:EnumPeriodoLiquidacion'
                ).filter(
                  option =>
                    option.value !== EnumPeriodoLiquidacion.Horario &&
                    option.value !== EnumPeriodoLiquidacion.Diario &&
                    option.value !== EnumPeriodoLiquidacion.Indefinido
                ),
                onChange(value, formState) {
                  setSelectedCoefficient(prevState => ({
                    ...prevState,
                    idSettlementPeriod: +value
                  }))
                }
              },
              {
                name: 'year',
                label: t('priceComponent:forms.coefficientAssistant.inputs.year'),
                type: DataType.DATE,
                views: ['year'],
                format: 'YYYY',
                defaultValue: moment(new Date()),
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const startDate = moment(value).startOf('year').toDate()
                    const endDate = moment(value).endOf('year').toDate()

                    setSelectedCoefficient(prevState => ({
                      ...prevState,
                      year: moment(value).toDate(),
                      startDate,
                      endDate
                    }))
                  }
                }
              }
            ]
          }
        ]
      }
    }, [])

  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    setTableData(priceCoefficientsList ?? [])
  }, [priceCoefficientsList])

  useEffect(() => {
    updateFormDefinition(coefficientAssistantDefinition, selectedCoefficient)
  }, [coefficientAssistantDefinition])

  useEffect(() => {
    updateFormDefinition(addManualDefinition, selectedCoefficient)
  }, [addManualDefinition])

  // #endregion Hooks Ends

  // #region Footer Actions Starts
  const splitButtonOptions: ButtonOption[] = [
    {
      id: 1,
      name: 'ASSISTED',
      label: t('priceComponent:mainActions.addAssisted')
    },
    {
      id: 2,
      name: 'MANUAL',
      label: t('priceComponent:mainActions.addManual')
    }
  ]

  const footerActions: CardActionProps[] = [
    {
      splitButton: {
        options: splitButtonOptions,
        onClick: handleOnAddCoefficients
      }
    },
    {
      label: t('priceCoefficient:globalNotifications.retries.actions.save'),
      handleActionClick: onSaveHandlerClick,
      size: 'small',
      color: 'primary',
      variant: 'contained',
      visibleIf: showSaveHandler,
      disabled: saveButtonDisabled
    }
  ]
  // #endregion Footer Actions Ends

  return (
    <>
      <StandardCard
        title={t('priceCoefficient:title')}
        showEmptyContent={noData}
        isLoading={isLoading}
        splitButtonOptions={splitButtonOptions}
        onSplitButtonClick={handleOnAddCoefficients}
        footerActions={noData ? [] : footerActions}
        {...props}
      >
        <PriceCoefficientsTable
          noTitle
          priceCoefficients={tableData}
          round={rounding}
          onNotificationChange={() => {}}
          onEditRowValues={row => handleEditRow(row)}
          onDeleteRow={row => handleDeleteRow(row)}
        />
      </StandardCard>
      <CustomFormDialog {...formProps} />
    </>
  )
}
